/**
 * Startup / Founder Cockpit — management-style dashboard.
 *
 * Sections:
 *   1. Hero — founder greeting + 4 hero KPIs (Runway, Cash, MRR, Burn)
 *      with sparklines.
 *   2. Fundraising progress — round target + progress bar + 3 lead
 *      investors badges.
 *   3. Iqra AI Co-pilot — 3 tactical suggestions for this week.
 *   4. North-Star KPI tracker — DAU / Activation / Retention sparklines.
 *   5. Action items — overdue/pending things the founder owes someone.
 *   6. Mentor + investor activity feed — last 5 events.
 *   7. Next milestone card — biggest upcoming deliverable + countdown.
 *
 * Data: GET /startups/mine, /startups/agreements, /meetings/. Falls back
 * to demo data when the API returns empty (purged on GO LIVE).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket, Sparkles, TrendingUp, Target, Wallet, Users,
  ChevronRight, MessagesSquare, CalendarDays, AlertTriangle,
  Flame, BookOpen, Activity, Heart, ArrowUpRight, ArrowDownRight,
  Clock, CheckCircle2, FileSignature,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

function fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function Sparkline({ data, color = '#8B3FC5', height = 26, width = 88 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = Math.max(1, max - min);
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * step},${height - ((v - min) / range) * height}`).join(' ');
  const lastY = height - ((data[data.length - 1] - min) / range) * height;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={pts}/>
      <circle cx={(data.length - 1) * step} cy={lastY} r="2.5" fill={color}/>
    </svg>
  );
}

function KpiTile({ label, value, delta, sparkData, icon: Icon, accent, testid, deltaInverted = false }) {
  const isUp = (delta ?? 0) >= 0;
  const positive = deltaInverted ? !isUp : isUp;
  return (
    <div
      className="ve-card p-5 group transition-all hover:border-[var(--ve-violet)] hover:shadow-lg relative overflow-hidden"
      data-testid={testid}
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ backgroundColor: accent }}/>
      <div className="flex items-start justify-between mt-1">
        <span className="text-[10px] uppercase tracking-[0.18em] opacity-65">{label}</span>
        <span
          className="h-9 w-9 rounded-md flex items-center justify-center transition-transform group-hover:scale-110 border"
          style={{ backgroundColor: `${accent}1a`, color: accent, borderColor: `${accent}40` }}
        >
          <Icon size={15} strokeWidth={1.5}/>
        </span>
      </div>
      <div className="display text-3xl mt-3 tabular-nums font-bold">{value}</div>
      <div className="flex items-end justify-between mt-2.5 gap-2">
        {delta !== undefined && delta !== null ? (
          <div className={`text-[11px] flex items-center gap-1 tabular-nums font-bold ${positive ? 'text-emerald-500' : 'text-rose-500'}`}>
            {isUp ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
            {isUp ? '+' : ''}{delta.toFixed(1)}% MoM
          </div>
        ) : <div className="text-[11px] opacity-50">—</div>}
        {sparkData && <Sparkline data={sparkData} color={accent}/>}
      </div>
    </div>
  );
}

export default function StartupDashboard() {
  const { user } = useAuth();
  const [myStartup, setMyStartup] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [s, a, m] = await Promise.all([
          api.get('/startups/mine').catch(() => ({ data: null })),
          api.get('/startups/agreements').catch(() => ({ data: [] })),
          api.get('/meetings/').catch(() => ({ data: [] })),
        ]);
        if (cancel) return;
        queueMicrotask(() => {
          setMyStartup(s.data);
          setAgreements(a.data || []);
          setMeetings(m.data || []);
          setLoading(false);
        });
      } catch {
        if (cancel) return;
        queueMicrotask(() => setLoading(false));
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Demo seed for the demo tenant — purged on GO LIVE.
  const data = useMemo(() => ({
    name: myStartup?.name || 'Sahel Robotics',
    sector: myStartup?.sector || 'Industrial Robotics',
    stage: myStartup?.stage || 'Series A',
    runway: 9,
    runwayDelta: -1.2,
    cash: 2_400_000,
    cashDelta: -8.5,
    mrr: 142_000,
    mrrDelta: 14.3,
    burn: 280_000,
    burnDelta: -3.4,
    sparkRunway:  [12, 11, 11, 10, 10, 9.5, 9],
    sparkCash:    [3.2, 3.0, 2.85, 2.7, 2.6, 2.5, 2.4],
    sparkMrr:     [98, 105, 112, 120, 128, 135, 142],
    sparkBurn:    [310, 305, 298, 292, 286, 282, 280],
    round: {
      target: 8_000_000,
      raised: 5_200_000,
      stage: 'Series A',
      leads: [
        { name: 'Falaj Ventures', soft: true },
        { name: 'Pearl Capital',  soft: true },
        { name: 'Iqra Fund I',    soft: false },
      ],
    },
    kpis: {
      dau:        { value: 12_800, delta: 18.4, spark: [7500, 8200, 9100, 10100, 11000, 11900, 12800], accent: '#10B981' },
      activation: { value: 64,     delta: 4.2,  spark: [52, 54, 56, 58, 60, 62, 64],                   accent: '#8B3FC5' },
      retention:  { value: 78,     delta: 2.1,  spark: [72, 73, 74, 76, 76, 77, 78],                   accent: '#0EA5E9' },
    },
    nextMilestone: {
      title: 'Q1 Board Pack + Term Sheet',
      due_in: '6 days',
      progress: 70,
    },
  }), [myStartup]);

  const roundProgress = Math.round((data.round.raised / data.round.target) * 100);

  const upcoming = (meetings || []).slice(0, 3);

  return (
    <div className="space-y-7" data-testid="startup-cockpit">
      {/* ── 0. Hero greeting ── */}
      <div className="ve-card p-6 flex items-start gap-5 flex-wrap">
        <div className="h-14 w-14 rounded-md flex items-center justify-center shrink-0"
             style={{ background: 'rgba(174,82,234,0.15)', color: 'var(--ve-violet-bright)' }}>
          <Rocket size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="ve-eyebrow">Founder Cockpit · {data.stage}</div>
          <h1 className="display text-2xl mt-1" data-testid="startup-greeting">
            {data.name} · {user?.first_name || user?.email?.split('@')[0] || 'Founder'}
          </h1>
          <p className="text-sm opacity-70 mt-1 max-w-3xl">
            {data.runway} months of runway · MRR up {data.mrrDelta.toFixed(1)}% MoM · next milestone in {data.nextMilestone.due_in}.
          </p>
        </div>
        <div className="flex flex-col gap-2 items-stretch">
          <Link to="/dashboard/founder-vetting" className="ve-btn-primary text-[11px] inline-flex items-center gap-1.5 justify-center">
            Run AI vetting <Sparkles size={11}/>
          </Link>
          <Link to="/dashboard/chat" className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ve-violet-bright)] hover:underline text-center">
            Chat with mentors →
          </Link>
        </div>
      </div>

      {/* ── 1. KPI strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile testid="kpi-runway"  label="Runway"  value={`${data.runway} mo`}    delta={data.runwayDelta}  sparkData={data.sparkRunway}  icon={Clock}    accent="#F59E0B" deltaInverted/>
        <KpiTile testid="kpi-cash"    label="Cash on hand"  value={fmtMoney(data.cash)} delta={data.cashDelta}  sparkData={data.sparkCash}    icon={Wallet}   accent="#10B981" deltaInverted/>
        <KpiTile testid="kpi-mrr"     label="MRR"           value={fmtMoney(data.mrr)}  delta={data.mrrDelta}   sparkData={data.sparkMrr}     icon={TrendingUp} accent="#8B3FC5"/>
        <KpiTile testid="kpi-burn"    label="Burn rate"     value={fmtMoney(data.burn)} delta={data.burnDelta}  sparkData={data.sparkBurn}    icon={Activity} accent="#EF4444" deltaInverted/>
      </div>

      {/* ── 2. Fundraising progress + Iqra Co-pilot ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4">
        <div className="ve-card p-5" data-testid="startup-fundraising">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="ve-eyebrow">Fundraising · {data.round.stage}</div>
              <h3 className="display text-base mt-1">{fmtMoney(data.round.raised)} of {fmtMoney(data.round.target)} raised</h3>
            </div>
            <FileSignature size={18} className="opacity-50"/>
          </div>
          <div className="h-3 rounded-full overflow-hidden bg-[rgba(255,255,255,0.05)]">
            <div className="h-full rounded-full transition-all" style={{ width: `${roundProgress}%`, background: 'linear-gradient(90deg, #8B3FC5 0%, #AE52EA 100%)' }}/>
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px]">
            <span className="font-bold text-[var(--ve-violet-bright)] pal-mono">{roundProgress}%</span>
            <span className="opacity-65">{fmtMoney(data.round.target - data.round.raised)} remaining</span>
          </div>
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-65 mb-2">Lead investors</div>
            <div className="flex flex-wrap gap-2">
              {data.round.leads.map((l) => (
                <div key={l.name} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-bold ${
                  l.soft ? 'border-amber-400/40 text-amber-500 bg-amber-500/[0.08]' : 'border-emerald-400/40 text-emerald-500 bg-emerald-500/[0.08]'
                }`}>
                  {l.soft ? <Heart size={11}/> : <CheckCircle2 size={11}/>}
                  {l.name}
                  <span className="opacity-65 font-normal">{l.soft ? 'soft' : 'committed'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ve-card p-5" data-testid="startup-iqra-copilot">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="ve-eyebrow">Iqra AI · Co-pilot</div>
              <h3 className="display text-base mt-1">This week&apos;s tactical plays</h3>
            </div>
            <Sparkles size={18} className="text-[var(--ve-violet-bright)]"/>
          </div>
          <div className="space-y-3 text-[13px]">
            <div className="p-3 rounded border-l-2 border-emerald-500 bg-emerald-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-500 mb-1 flex items-center gap-1.5"><Flame size={11}/> Momentum</div>
              <span className="opacity-90">MRR growth accelerated. Push Falaj Ventures for a hard commit — your traction beats their thesis.</span>
            </div>
            <div className="p-3 rounded border-l-2 border-amber-500 bg-amber-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-500 mb-1 flex items-center gap-1.5"><AlertTriangle size={11}/> Runway</div>
              <span className="opacity-90">9 months left at current burn. Negotiate a 30-day bridge or close round by week 4.</span>
            </div>
            <div className="p-3 rounded border-l-2 border-sky-500 bg-sky-500/[0.06]">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-sky-500 mb-1 flex items-center gap-1.5"><Target size={11}/> Product</div>
              <span className="opacity-90">Activation up 4.2pp — push the new onboarding live to 100% next week.</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3. North-Star KPIs ── */}
      <div className="ve-card p-5" data-testid="startup-north-star">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="ve-eyebrow">North-Star Metrics</div>
            <h3 className="display text-base mt-1">User growth · 7-day trailing</h3>
          </div>
          <Target size={18} className="opacity-50"/>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            ['Daily active users', 'dau', '', ''],
            ['Activation rate',    'activation', '%', '/100'],
            ['7-day retention',    'retention',  '%', '/100'],
          ].map(([label, key, suffix]) => {
            const k = data.kpis[key];
            return (
              <div key={key} className="rounded border border-[rgba(174,82,234,0.18)] p-4" data-testid={`north-star-${key}`}>
                <div className="text-[10px] uppercase tracking-[0.18em] opacity-65">{label}</div>
                <div className="flex items-end justify-between mt-2">
                  <div className="display text-2xl tabular-nums font-bold">{k.value.toLocaleString()}{suffix}</div>
                  <Sparkline data={k.spark} color={k.accent} width={70} height={26}/>
                </div>
                <div className={`text-[11px] mt-1 font-bold ${k.delta >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {k.delta >= 0 ? '+' : ''}{k.delta.toFixed(1)}pp WoW
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 4. Action items + Activity feed ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ve-card p-5" data-testid="startup-actions">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="ve-eyebrow">Action items</div>
              <h3 className="display text-base mt-1">5 things you owe</h3>
            </div>
            <Clock size={18} className="opacity-50"/>
          </div>
          <ul className="space-y-2 text-[13px]">
            {[
              { tone: 'rose',    icon: AlertTriangle, label: 'Sign Falaj Ventures term sheet',    due: 'today' },
              { tone: 'amber',   icon: FileSignature, label: 'Submit Q1 board pack draft',         due: 'in 2 days' },
              { tone: 'sky',     icon: MessagesSquare, label: 'Reply to Pearl Capital diligence Qs', due: 'in 4 days' },
              { tone: 'emerald', icon: BookOpen,      label: 'Update KPI dashboard for Iqra Fund', due: 'in 5 days' },
              { tone: 'violet',  icon: Users,         label: 'Onboard 2 senior engineers',         due: 'this week' },
            ].map((a, i) => {
              const I = a.icon;
              return (
                <li key={i} className={`flex items-start gap-2.5 p-2.5 rounded border-l-2 border-${a.tone}-500 bg-${a.tone}-500/[0.04]`}>
                  <I size={13} className={`text-${a.tone}-500 mt-0.5 shrink-0`}/>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium opacity-90 truncate">{a.label}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] opacity-50 mt-0.5">{a.due}</div>
                  </div>
                  <ChevronRight size={12} className="opacity-40 mt-1 shrink-0"/>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="ve-card p-5" data-testid="startup-activity-feed">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="ve-eyebrow">Mentor &amp; investor activity</div>
              <h3 className="display text-base mt-1">Last 5 signals</h3>
            </div>
            <Activity size={18} className="opacity-50"/>
          </div>
          <ul className="space-y-3 text-[13px]">
            {[
              { who: 'Falaj Ventures',  what: 'Opened your deck · spent 4m 12s',          when: '2h ago', tone: '#8B3FC5' },
              { who: 'Mentor · Karim',   what: 'Suggested intro to Sahel Holding board',   when: '6h ago', tone: '#10B981' },
              { who: 'Pearl Capital',    what: 'Forwarded financials to their analyst',     when: '1d ago', tone: '#0EA5E9' },
              { who: 'Iqra Fund I',      what: 'Asked for updated cap table',              when: '2d ago', tone: '#F59E0B' },
              { who: 'Mentor · Layla',   what: 'Endorsed your CTO on Shura Nexus',         when: '3d ago', tone: '#EC4899' },
            ].map((e, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${e.tone}22`, color: e.tone }}>
                  <Activity size={11}/>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{e.who}</div>
                  <div className="text-[12px] opacity-75 truncate">{e.what}</div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.16em] opacity-50 shrink-0">{e.when}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── 5. Next milestone ── */}
      <div className="ve-card p-5 flex items-center gap-5 flex-wrap" data-testid="startup-next-milestone">
        <div className="h-14 w-14 rounded-md flex items-center justify-center shrink-0" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
          <CalendarDays size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="ve-eyebrow">Next milestone</div>
          <h3 className="display text-lg mt-1">{data.nextMilestone.title}</h3>
          <div className="mt-2 h-2 rounded-full overflow-hidden bg-[rgba(255,255,255,0.05)]">
            <div className="h-full rounded-full" style={{ width: `${data.nextMilestone.progress}%`, background: 'linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)' }}/>
          </div>
          <div className="text-[11px] mt-1.5 opacity-65 flex items-center justify-between">
            <span>{data.nextMilestone.progress}% complete</span>
            <span>Due in {data.nextMilestone.due_in}</span>
          </div>
        </div>
        <Link to="/dashboard/minutes" className="ve-btn-primary text-[11px] inline-flex items-center gap-1.5">
          Open board pack <ChevronRight size={12}/>
        </Link>
      </div>

      {loading && (
        <div className="text-center text-[11px] opacity-50">Refreshing cockpit…</div>
      )}
    </div>
  );
}
